import { 
  getSchedulesByInvoiceId,
  updateSchedule,
  subscribeToSchedules 
} from '../firebase/scheduleService';
import { 
  refreshMaterialListAvailability,
  updateReservationDates,
  RESERVATION_STATUS 
} from '../firebase/materialListService';
import { 
  checkSmartAvailability,
  getEffectiveDates 
} from './enhancedAvailabilitySystem';

/**
 * Schedule Integration Utilities
 * Handles synchronization between schedules and material lists
 */

// Schedule status constants
export const SCHEDULE_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

// Job type constants for material requirements
export const JOB_TYPES = {
  DELIVERY: 'delivery',
  INSTALLATION: 'installation',
  INSPECTION: 'inspection',
  DISMANTLING: 'dismantling',
  COLLECTION: 'collection',
  MAINTENANCE: 'maintenance'
};

// Material impact by job type
export const JOB_TYPE_MATERIAL_IMPACT = {
  [JOB_TYPES.DELIVERY]: {
    requiresMaterials: true,
    statusTransition: 'available_to_transit',
    priority: 1
  },
  [JOB_TYPES.INSTALLATION]: {
    requiresMaterials: true,
    statusTransition: 'transit_to_deployed',
    priority: 1
  },
  [JOB_TYPES.INSPECTION]: {
    requiresMaterials: false,
    statusTransition: null,
    priority: 3
  },
  [JOB_TYPES.DISMANTLING]: {
    requiresMaterials: true,
    statusTransition: 'deployed_to_transit',
    priority: 2
  },
  [JOB_TYPES.COLLECTION]: {
    requiresMaterials: true,
    statusTransition: 'transit_to_available',
    priority: 2
  },
  [JOB_TYPES.MAINTENANCE]: {
    requiresMaterials: false,
    statusTransition: null,
    priority: 3
  }
};

/**
 * Load schedules related to an invoice
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Array>} Array of related schedules
 */
export const loadRelatedSchedules = async (invoiceId) => {
  try {
    console.log(`📅 Loading schedules for invoice: ${invoiceId}`);
    
    const schedules = await getSchedulesByInvoiceId(invoiceId);
    
    // Sort schedules by date and priority
    const sortedSchedules = schedules.sort((a, b) => {
      // First sort by date
      const dateA = new Date(a.scheduledDate || a.createdAt);
      const dateB = new Date(b.scheduledDate || b.createdAt);
      
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // Then by job type priority
      const priorityA = JOB_TYPE_MATERIAL_IMPACT[a.jobType]?.priority || 999;
      const priorityB = JOB_TYPE_MATERIAL_IMPACT[b.jobType]?.priority || 999;
      
      return priorityA - priorityB;
    });

    console.log(`✅ Loaded ${sortedSchedules.length} schedules for invoice ${invoiceId}`);
    return sortedSchedules;
    
  } catch (error) {
    console.error(`❌ Error loading schedules for invoice ${invoiceId}:`, error);
    return [];
  }
};

/**
 * Get material-impacting schedules only
 * @param {Array} schedules - All schedules
 * @returns {Array} Schedules that impact material availability
 */
export const getMaterialImpactingSchedules = (schedules) => {
  return schedules.filter(schedule => {
    const impact = JOB_TYPE_MATERIAL_IMPACT[schedule.jobType];
    return impact && impact.requiresMaterials;
  });
};

/**
 * Calculate effective date ranges from schedules
 * @param {Array} schedules - Array of schedules
 * @returns {Array} Array of date ranges with metadata
 */
export const calculateScheduleDateRanges = (schedules) => {
  const dateRanges = [];
  
  // Group schedules by date
  const schedulesByDate = {};
  
  schedules.forEach(schedule => {
    if (!schedule.scheduledDate) return;
    
    const dateKey = schedule.scheduledDate;
    if (!schedulesByDate[dateKey]) {
      schedulesByDate[dateKey] = [];
    }
    schedulesByDate[dateKey].push(schedule);
  });
  
  // Create date ranges
  Object.entries(schedulesByDate).forEach(([date, daySchedules]) => {
    const materialImpactingSchedules = getMaterialImpactingSchedules(daySchedules);
    
    if (materialImpactingSchedules.length > 0) {
      dateRanges.push({
        startDate: date,
        endDate: date, // Single day for now
        schedules: daySchedules,
        materialSchedules: materialImpactingSchedules,
        jobTypes: [...new Set(materialImpactingSchedules.map(s => s.jobType))],
        status: getDateRangeStatus(daySchedules)
      });
    }
  });
  
  return dateRanges.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
};

/**
 * Get status for a date range based on its schedules
 * @param {Array} schedules - Schedules for the date
 * @returns {string} Overall status
 */
const getDateRangeStatus = (schedules) => {
  const statuses = schedules.map(s => s.status);
  
  if (statuses.includes(SCHEDULE_STATUS.CANCELLED)) {
    return SCHEDULE_STATUS.CANCELLED;
  }
  if (statuses.includes(SCHEDULE_STATUS.COMPLETED)) {
    return SCHEDULE_STATUS.COMPLETED;
  }
  if (statuses.includes(SCHEDULE_STATUS.IN_PROGRESS)) {
    return SCHEDULE_STATUS.IN_PROGRESS;
  }
  if (statuses.includes(SCHEDULE_STATUS.CONFIRMED)) {
    return SCHEDULE_STATUS.CONFIRMED;
  }
  
  return SCHEDULE_STATUS.PENDING;
};

/**
 * Sync material list availability when schedules change
 * @param {string} invoiceId - Invoice ID
 * @param {string} materialListId - Material list ID
 * @param {Array} updatedSchedules - Updated schedules array
 * @returns {Promise<boolean>} Success status
 */
export const syncMaterialAvailabilityWithSchedules = async (invoiceId, materialListId, updatedSchedules) => {
  try {
    console.log(`🔄 Syncing material availability for invoice ${invoiceId}`);
    
    // Refresh material list availability with updated schedules
    const success = await refreshMaterialListAvailability(materialListId, updatedSchedules);
    
    if (success) {
      console.log(`✅ Material availability synced successfully`);
      
      // Update reservations if needed
      await updateReservationsFromSchedules(materialListId, updatedSchedules);
      
      return true;
    }
    
    return false;
    
  } catch (error) {
    console.error(`❌ Error syncing material availability:`, error);
    return false;
  }
};

/**
 * Update inventory reservations based on schedule changes
 * @param {string} materialListId - Material list ID
 * @param {Array} schedules - Updated schedules
 * @returns {Promise<boolean>} Success status
 */
const updateReservationsFromSchedules = async (materialListId, schedules) => {
  try {
    const dateRanges = calculateScheduleDateRanges(schedules);
    
    if (dateRanges.length === 0) {
      console.log('⚠️ No material-impacting schedules found');
      return true;
    }
    
    // For now, use the first date range (can be enhanced for multiple ranges)
    const primaryDateRange = dateRanges[0];
    
    // Update reservation dates
    // Note: This assumes there's a reservation ID available
    // In a full implementation, we'd need to track reservation IDs
    console.log(`📅 Would update reservation dates to: ${primaryDateRange.startDate} - ${primaryDateRange.endDate}`);
    
    return true;
    
  } catch (error) {
    console.error('❌ Error updating reservations from schedules:', error);
    return false;
  }
};

/**
 * Create real-time schedule subscription for material list updates
 * @param {string} invoiceId - Invoice ID
 * @param {string} materialListId - Material list ID
 * @param {Function} onScheduleChange - Callback when schedules change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToScheduleChanges = (invoiceId, materialListId, onScheduleChange) => {
  try {
    console.log(`👂 Setting up schedule subscription for invoice: ${invoiceId}`);
    
    return subscribeToSchedules((schedules) => {
      // Filter schedules for this invoice
      const relatedSchedules = schedules.filter(schedule => 
        schedule.invoiceId === invoiceId
      );
      
      console.log(`📅 Schedule update received: ${relatedSchedules.length} schedules`);
      
      // Trigger callback with updated schedules
      onScheduleChange(relatedSchedules);
      
      // Auto-sync material availability
      if (materialListId) {
        syncMaterialAvailabilityWithSchedules(invoiceId, materialListId, relatedSchedules);
      }
    }, { invoiceId });
    
  } catch (error) {
    console.error('❌ Error setting up schedule subscription:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Detect schedule conflicts with material availability
 * @param {Array} schedules - Array of schedules
 * @param {Object} materialRequirements - Material requirements
 * @returns {Promise<Object>} Conflict analysis
 */
export const detectScheduleConflicts = async (schedules, materialRequirements) => {
  try {
    console.log('🔍 Detecting schedule conflicts with material availability');
    
    const conflicts = [];
    const warnings = [];
    
    const dateRanges = calculateScheduleDateRanges(schedules);
    
    for (const dateRange of dateRanges) {
      // Check material availability for this date range
      const availabilityCheck = await checkSmartAvailability(
        materialRequirements,
        { dates: [{ type: 'date', value: dateRange.startDate }] }, // Mock invoice structure
        schedules
      );
      
      if (!availabilityCheck.isAvailable) {
        conflicts.push({
          date: dateRange.startDate,
          schedules: dateRange.schedules,
          availabilityIssues: availabilityCheck.availabilityResults,
          severity: 'high'
        });
      }
      
      // Check for scheduling logic issues
      const logicWarnings = validateScheduleLogic(dateRange.schedules);
      warnings.push(...logicWarnings);
    }
    
    return {
      hasConflicts: conflicts.length > 0,
      hasWarnings: warnings.length > 0,
      conflicts,
      warnings,
      totalSchedules: schedules.length,
      materialImpactingSchedules: getMaterialImpactingSchedules(schedules).length
    };
    
  } catch (error) {
    console.error('❌ Error detecting schedule conflicts:', error);
    return {
      hasConflicts: false,
      hasWarnings: false,
      conflicts: [],
      warnings: [],
      error: error.message
    };
  }
};

/**
 * Validate schedule logic (e.g., delivery before installation)
 * @param {Array} schedules - Schedules for a specific date
 * @returns {Array} Array of warnings
 */
const validateScheduleLogic = (schedules) => {
  const warnings = [];
  
  const jobTypes = schedules.map(s => s.jobType);
  
  // Check if installation is scheduled without delivery
  if (jobTypes.includes(JOB_TYPES.INSTALLATION) && !jobTypes.includes(JOB_TYPES.DELIVERY)) {
    warnings.push({
      type: 'logic_warning',
      message: 'Installation scheduled without delivery on same day',
      severity: 'medium',
      suggestion: 'Consider scheduling delivery before installation'
    });
  }
  
  // Check if collection is scheduled without dismantling
  if (jobTypes.includes(JOB_TYPES.COLLECTION) && !jobTypes.includes(JOB_TYPES.DISMANTLING)) {
    warnings.push({
      type: 'logic_warning',
      message: 'Collection scheduled without dismantling',
      severity: 'medium',
      suggestion: 'Ensure dismantling is completed before collection'
    });
  }
  
  // Check for conflicting job types on same day
  const conflictingPairs = [
    [JOB_TYPES.INSTALLATION, JOB_TYPES.DISMANTLING],
    [JOB_TYPES.DELIVERY, JOB_TYPES.COLLECTION]
  ];
  
  conflictingPairs.forEach(([type1, type2]) => {
    if (jobTypes.includes(type1) && jobTypes.includes(type2)) {
      warnings.push({
        type: 'conflict_warning',
        message: `Conflicting job types scheduled: ${type1} and ${type2}`,
        severity: 'high',
        suggestion: 'Review schedule to avoid conflicting operations'
      });
    }
  });
  
  return warnings;
};

/**
 * Generate schedule recommendations based on material requirements
 * @param {Object} materialRequirements - Material requirements
 * @param {Array} existingSchedules - Existing schedules
 * @returns {Array} Array of schedule recommendations
 */
export const generateScheduleRecommendations = (materialRequirements, existingSchedules = []) => {
  const recommendations = [];
  
  try {
    const materialTypes = Object.keys(materialRequirements);
    const hasExistingSchedules = existingSchedules.length > 0;
    
    // Recommend delivery schedule if materials are required
    if (materialTypes.length > 0 && !hasExistingSchedules) {
      recommendations.push({
        type: 'schedule_recommendation',
        jobType: JOB_TYPES.DELIVERY,
        priority: 'high',
        message: 'Schedule delivery for required materials',
        estimatedDuration: '2-4 hours',
        prerequisites: []
      });
      
      recommendations.push({
        type: 'schedule_recommendation',
        jobType: JOB_TYPES.INSTALLATION,
        priority: 'high',
        message: 'Schedule installation after delivery',
        estimatedDuration: '4-8 hours',
        prerequisites: [JOB_TYPES.DELIVERY]
      });
    }
    
    // Recommend inspection for rental items
    const hasRentalItems = materialTypes.some(type => 
      type.toLowerCase().includes('rental') || 
      materialRequirements[type].isRental
    );
    
    if (hasRentalItems) {
      recommendations.push({
        type: 'schedule_recommendation',
        jobType: JOB_TYPES.INSPECTION,
        priority: 'medium',
        message: 'Schedule weekly inspection for rental items',
        estimatedDuration: '1-2 hours',
        prerequisites: [JOB_TYPES.INSTALLATION],
        recurring: 'weekly'
      });
    }
    
    return recommendations;
    
  } catch (error) {
    console.error('❌ Error generating schedule recommendations:', error);
    return [];
  }
};

/**
 * Calculate optimal schedule sequence
 * @param {Array} requiredJobTypes - Required job types
 * @param {Date} startDate - Preferred start date
 * @returns {Array} Optimized schedule sequence
 */
export const calculateOptimalScheduleSequence = (requiredJobTypes, startDate = new Date()) => {
  try {
    const sequence = [];
    
    // Define optimal job sequence
    const optimalSequence = [
      JOB_TYPES.DELIVERY,
      JOB_TYPES.INSTALLATION,
      JOB_TYPES.INSPECTION,
      JOB_TYPES.MAINTENANCE,
      JOB_TYPES.DISMANTLING,
      JOB_TYPES.COLLECTION
    ];
    
    // Filter and order based on required job types
    const orderedJobs = optimalSequence.filter(jobType => 
      requiredJobTypes.includes(jobType)
    );
    
    let currentDate = new Date(startDate);
    
    orderedJobs.forEach((jobType, index) => {
      const impact = JOB_TYPE_MATERIAL_IMPACT[jobType];
      
      sequence.push({
        jobType,
        recommendedDate: new Date(currentDate),
        priority: impact?.priority || 3,
        estimatedDuration: getEstimatedDuration(jobType),
        dependencies: index > 0 ? [orderedJobs[index - 1]] : [],
        materialImpact: impact?.requiresMaterials || false
      });
      
      // Add buffer time between jobs
      currentDate.setDate(currentDate.getDate() + getJobBufferDays(jobType));
    });
    
    return sequence;
    
  } catch (error) {
    console.error('❌ Error calculating optimal schedule sequence:', error);
    return [];
  }
};

/**
 * Get estimated duration for job type
 * @param {string} jobType - Job type
 * @returns {string} Estimated duration
 */
const getEstimatedDuration = (jobType) => {
  const durations = {
    [JOB_TYPES.DELIVERY]: '2-4 hours',
    [JOB_TYPES.INSTALLATION]: '4-8 hours',
    [JOB_TYPES.INSPECTION]: '1-2 hours',
    [JOB_TYPES.DISMANTLING]: '3-6 hours',
    [JOB_TYPES.COLLECTION]: '2-4 hours',
    [JOB_TYPES.MAINTENANCE]: '1-3 hours'
  };
  
  return durations[jobType] || '2-4 hours';
};

/**
 * Get buffer days between job types
 * @param {string} jobType - Job type
 * @returns {number} Buffer days
 */
const getJobBufferDays = (jobType) => {
  const buffers = {
    [JOB_TYPES.DELIVERY]: 0, // Same day as installation possible
    [JOB_TYPES.INSTALLATION]: 1, // 1 day buffer
    [JOB_TYPES.INSPECTION]: 7, // Weekly inspection
    [JOB_TYPES.DISMANTLING]: 1, // 1 day buffer
    [JOB_TYPES.COLLECTION]: 0, // Same day as dismantling possible
    [JOB_TYPES.MAINTENANCE]: 7 // Weekly maintenance
  };
  
  return buffers[jobType] || 1;
};
